import { makeStyles } from "@material-ui/core";

const useStyle = makeStyles((theme) => ({

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYOUT RAIZ — 4 colunas: status | faturamento | embalagem | embarcados
  // ═══════════════════════════════════════════════════════════════════════════
  fullWidth: {
    display: "grid",
    // A coluna de status só precisa caber dois números; o espaço que sobra dela
    // vai igualmente para as três colunas de etapa, que têm tabela.
    gridTemplateColumns: "minmax(150px, 0.48fr) repeat(3, 1fr)",
    height: "100vh",
    padding: 15,
    gap: 15,
    boxSizing: "border-box",
    overflow: "hidden",

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
      height: "auto",
      overflow: "visible",
    },
  },

  // Coluna 1 — os três cards de status, empilhados
  statusColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    minHeight: 0,
  },

  statusCard: {
    flex: 1,
    minHeight: 0,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "8px 10px",

    "& h1": {
      textAlign: "center",
      fontSize: "clamp(13px, 1.3vw, 25px)",
      fontWeight: "bold",
      marginBottom: 20,
      [theme.breakpoints.down("md")]: { fontSize: 18 },
    },
    "& .values": {
      display: "flex",
      flexDirection: "column",
      gap: 25,
      textAlign: "center",
    },
    "& .label": {
      fontSize: "clamp(10px, 0.83vw, 16px)",
      fontWeight: "bold",
      lineHeight: 1.1,
      [theme.breakpoints.down("md")]: { fontSize: 9 },
    },
    "& .value": {
      fontSize: "clamp(22px, 2.34vw, 45px)",
      fontWeight: "bold",
      lineHeight: 1.05,
      [theme.breakpoints.down("md")]: { fontSize: 30 },
    },
  },

  // Colunas 2-4 — etapa em cima, fila embaixo
  stageColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    minHeight: 0,

    [theme.breakpoints.down("md")]: { height: "max-content" },
  },

  // Quadrante de etapa: título + tabela hora a hora + 4 totais
  stageBox: {
    flex: "1 1 75%",
    minHeight: 0,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    paddingBottom: 12,
    overflow: "hidden",

    "& h1": {
      width: "100%",
      textAlign: "center",
      // flexShrink:0 — sem ele o flex esmaga o título quando o conteúdo
      // abaixo não cabe, e o texto sai cortado ao meio.
      flexShrink: 0,
      fontSize: "clamp(15px, 1.35vw, 26px)",
      fontWeight: "bold",
      padding: "6px 0",
      [theme.breakpoints.down("md")]: { fontSize: 16 },
    },
  },

  stageContent: {
    display: "flex",
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },

  stageTotals: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    padding: "0 6px",
    gap: 50,

    "& .label": {
      fontSize: "clamp(9px, 0.68vw, 13px)",
      fontWeight: "bold",
      lineHeight: 1.1,
      [theme.breakpoints.down("md")]: { fontSize: 8 },
    },
    "& .value": {
      fontSize: "clamp(18px, 1.77vw, 34px)",
      fontWeight: "bold",
      lineHeight: 1.05,
      [theme.breakpoints.down("md")]: { fontSize: 18 },
    },

    // Previsão é projeção, não realizado — o itálico separa os dois à
    // distância, sem mexer no tamanho do número.
    "& .forecast .label": {
      fontStyle: "italic",
    },
  },

  // Quadrante de fila: título + quantidades + tabela de datas
  queueBox: {
    flex: "1 1 25%",
    minHeight: 0,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    paddingBottom: 10,
    overflow: "hidden",

    "& h1": {
      width: "100%",
      textAlign: "center",
      flexShrink: 0,
      fontSize: "clamp(14px, 1.35vw, 26px)",
      fontWeight: "bold",
      padding: "5px 0",
      [theme.breakpoints.down("md")]: { fontSize: 14 },
    },
  },

  queueContent: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    alignItems: 'center',
    overflow: "hidden",
  },

  queueTotals: {
    // Mesma proporção do stageTotals: 40% pros números, 60% pra tabela.
    width: "40%",
    display: "flex",
    flexDirection: "column",
    // space-evenly em vez de center + gap fixo: o gap de 35px somado às duas
    // caixas passava da altura do card em tela menor, e o "QTDE PEÇAS" saía
    // cortado no topo. Aqui o espaçamento é o que sobrar — igual ao stageTotals.
    justifyContent: "space-evenly",
    gap: 30,
    textAlign: "center",

    "& .label": {
      fontSize: "clamp(10px, 0.83vw, 16px)",
      fontWeight: "bold",
      lineHeight: 1.1,
      [theme.breakpoints.down("md")]: { fontSize: 8 },
    },
    "& .value": {
      fontSize: "clamp(20px, 2.08vw, 40px)",
      fontWeight: "bold",
      lineHeight: 1.05,
      [theme.breakpoints.down("md")]: { fontSize: 18 },
    },
  },

  /** Tela de carregamento — enquanto o 1º ciclo completo não termina */
  loadingScreen: {
    gridColumn: "1 / -1",
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,

    "& p": {
      fontSize: 18,
      fontWeight: 600,
      color: "#555555",
      letterSpacing: "0.02em",
    },
  },

  loadingSpinner: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    border: "5px solid #e2e5e9",
    borderTopColor: "#10B981",
    animation: "$spin 0.9s linear infinite",
  },

  "@keyframes spin": {
    to: { transform: "rotate(360deg)" },
  },

  // Cards de status: indicadores empilhados (QTDE PEÇAS em cima, PEDIDOS embaixo).
  // Os 3 cards (ESP. ETIQUETA / AG. INÍCIO / AUTORIZADO) compartilham a mesma
  // estrutura interna; só mudam a cor de fundo.

  /** Card ESP. ETIQUETA (âmbar) */
  cardWaitLabel: {
    background: "#ffb14e",
    color: "#fff",
  },

  /** Card AG. INÍCIO (laranja) */
  cardAgInicio: {
    background: "#ff9a66",
    color: "#fff",
  },

  /** Card AUTORIZADO (vermelho-salmão) */
  cardAuthorized: {
    background: "#ff7978",
    color: "#fff",
  },

  // ─── Tabela hora-a-hora ────────────────────────────────────────────────────
  boxTable: {
    width: "60%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,

    // Hora que ainda não chegou — apagada, pra não competir com o que já
    // aconteceu. Zero de hora vencida continua em destaque normal: é número.
    "& .pendingHour > span": {
      opacity: 0.45,
    },

    "& .scaleTitle": {
      display: "flex",
      flexShrink: 0,
      "& span": {
        flex: 1,
        // minWidth:0 é o que permite a célula encolher. Sem ele o item flex
        // trava no tamanho da palavra ("PEDIDOS"), e em tela estreita o
        // cabeçalho ficava mais largo que a tabela e desalinhava das linhas.
        minWidth: 0,
        padding: "4px 4px",
        textAlign: "center",
        fontSize: "clamp(9px, 0.83vw, 16px)",
        background: "#ffffff45",
        [theme.breakpoints.down("md")]: { fontSize: 7 },
      },
    },

    // Uma lista só — a faixa exibida é decidida pela rotação das abas, então
    // as linhas entram no fluxo normal e o excedente é cortado aqui.
    "& .boxScale": {
      display: "flex",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",

      // flex:1 aqui é largura (o .boxScale é row); a altura vem do height.
      "& .scale": {
        flex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },

      // A altura de cada linha é fixa e vem do HOUR_ROW_HEIGHT, aplicado no
      // render: sempre a da faixa cheia, então a tabela não estica conforme
      // as horas vão entrando ao longo do dia.
      "& .scale > p": {
        flexShrink: 0,
        alignItems: "center",
        display: "flex",
        "&:nth-child(even)": { background: "#ffffff45" },

        "& > span": {
          flex: 1,
          minWidth: 0,
          padding: "3px 4px",
          textAlign: "center",
          fontSize: "clamp(11px, 1.09vw, 21px)",
          [theme.breakpoints.down("md")]: { fontSize: 9 },
        },
      },
    },
  },

  // ─── Cores dos painéis principais ────────────────────────────────────────
  boxBilling: {
    background: "#cb6566",
    color: "#fff",
  },

  boxPackaging: {
    background: "#679966",
    color: "#fff",
  },

  // EMBARCADOS / AG. EMBARCAR — azul, fechando a sequência das etapas
  boxShipped: {
    background: "#4a7fa5",
    color: "#fff",
  },

  boxAgShipping: {
    background: "#7aa3c0",
    color: "#fff",
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AG. FATURAMENTO — vermelho (mesmo tom de Faturamento)
  // ═══════════════════════════════════════════════════════════════════════════
  boxAgBilling: {
    background: "#d88889",
    color: "#fff",

    "& $history": {
      "& > div": {
        "& > p": {
          "&:nth-child(odd)": { background: "#cb6566" },
        },
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AG. EMBALAGEM — verde (mesmo tom de Embalagem)
  // ═══════════════════════════════════════════════════════════════════════════
  boxAgPacking: {
    background: "#82ad82",
    color: "#fff",

    "& $history": {
      "& > div": {
        "& > p": {
          "&:nth-child(odd)": { background: "#679966" },
        },
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // HISTÓRICO — lista de datas em 2 colunas (ocupa os 40% restantes dos painéis AG)
  // ═══════════════════════════════════════════════════════════════════════════
  history: {
    // Listagem: as duas colunas começam na MESMA linha, no alto. Centralizado,
    // uma coluna com uma data só ficava boiando no meio do card, desalinhada
    // da primeira — e a tabela hora a hora do quadrante de cima começa no topo.
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "row",
    flex: 1,
    overflow: "hidden",

    "& > div": {
      width: "50%",
      height: "max-content",

      "&:first-child": {
        borderRight: "2px solid rgba(255,255,255,0.25)",
      },

      "& > p": {
        alignItems: "center",
        padding: "2px 5px",
        gap: 6,
        display: "flex",
        justifyContent: "space-between",

        "&:nth-child(odd)": {
          background: "rgba(0,0,0,0.12)",
        },

        // nowrap é trava, não estilo: "(11095 | 477)" quebrando em duas linhas
        // deixava as linhas com alturas diferentes e estourava o card inteiro.
        // Se um dia não couber, corta — não desmonta a listagem.
        "& > span": {
          whiteSpace: "nowrap",
          fontSize: "clamp(9px, 0.72vw, 14px)",
          [theme.breakpoints.down("md")]: { fontSize: 7 },

          "&:first-child": { fontWeight: "bold" },
        },
      },
    },
  },
}));

export default useStyle;
